import axios from 'axios';

// const API_URL = process.env.REACT_APP_API_URL || 'https://api.eyedealist.com/api';
const API_URL =process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL_PROD;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const isAuthRequest = error.config?.url?.includes('/auth/login');
      if (!isAuthRequest && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  getUsers: () => api.get('/auth/users'),
  createUser: (data) => api.post('/auth/users', data),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
};

// Patient API
export const patientAPI = {
  getPatients: (params) => api.get('/patients', { params }),
  getPatient: (id) => api.get(`/patients/${id}`),
  createPatient: (data) => api.post('/patients', data),
  updatePatient: (id, data) => api.put(`/patients/${id}`, data),
  deletePatient: (id) => api.delete(`/patients/${id}`),
  getPatientVisits: (id) => api.get(`/patients/${id}/visits`),
  getPatientHistory: (id) => api.get(`/patients/${id}/history`),
};

// Visit API
export const visitAPI = {
  getVisits: (params) => api.get('/visits', { params }),
  getVisit: (id) => api.get(`/visits/${id}`),
  createVisit: (data) => api.post('/visits', data),
  updateVisit: (id, data) => api.put(`/visits/${id}`, data),
  startStage: (id, data) => api.post(`/visits/${id}/start-stage`, data),
  endStage: (id, data) => api.post(`/visits/${id}/end-stage`, data),
  getTodayVisits: () => api.get('/visits/today'),
};

// Medicine API
export const medicineAPI = {
  getMedicines: (params) => api.get('/medicines', { params }),
  getMedicine: (id) => api.get(`/medicines/${id}`),
  createMedicine: (data) => api.post('/medicines', data),
  updateMedicine: (id, data) => api.put(`/medicines/${id}`, data),
  deleteMedicine: (id) => api.delete(`/medicines/${id}`),
};

// Optometry API
export const optometryAPI = {
  getRecords: (params) => api.get('/optometry', { params }),
  getRecord: (id) => api.get(`/optometry/${id}`),
  createRecord: (data) => api.post('/optometry', data),
  updateRecord: (id, data) => api.put(`/optometry/${id}`, data),
  deleteRecord: (id) => api.delete(`/optometry/${id}`),
};

// Consultation API
export const consultationAPI = {
  getConsultations: (params) => api.get('/consultations', { params }),
  getConsultation: (id) => api.get(`/consultations/${id}`),
  createConsultation: (data) => api.post('/consultations', data),
  updateConsultation: (id, data) => api.put(`/consultations/${id}`, data),
  deleteConsultation: (id) => api.delete(`/consultations/${id}`),
};

// Dispensing API
export const dispensingAPI = {
  getRecords: (params) => api.get('/dispensing', { params }),
  getRecord: (id) => api.get(`/dispensing/${id}`),
  createRecord: (data) => api.post('/dispensing', data),
  updateRecord: (id, data) => api.put(`/dispensing/${id}`, data),
  deleteRecord: (id) => api.delete(`/dispensing/${id}`),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getTimeTracking: (params) => api.get('/analytics/time-tracking', { params }),
  getPatientTimeTracking: (params) => api.get('/analytics/patient-time-tracking', { params }),
  getStaffPerformance: (params) => api.get('/analytics/staff-performance', { params }),
  getRevenue: (params) => api.get('/analytics/revenue', { params }),
};

// Invoice API
export const invoiceAPI = {
  getInvoices: (params) => api.get('/invoices', { params }),
  generateInvoice: (visitId) => api.get(`/invoices/visit/${visitId}`),
  listInvoices: (params) => api.get('/invoices/list', { params }),
  createInvoice: (data) => api.post('/invoices', data),
  getInvoiceById: (id) => api.get(`/invoices/${id}`),
};

// Appointment API
export const appointmentAPI = {
  getAppointments: (params) => api.get('/appointments', { params }),
  getTodayAppointments: () => api.get('/appointments/today'),
  getAppointment: (id) => api.get(`/appointments/${id}`),
  createAppointment: (data) => api.post('/appointments', data),
  updateAppointment: (id, data) => api.put(`/appointments/${id}`, data),
  cancelAppointment: (id) => api.delete(`/appointments/${id}`),
  checkInAppointment: (id, data) => api.post(`/appointments/${id}/check-in`, data),
};

// Fee Master API
export const feeConfigAPI = {
  getFeeConfigs: (params) => api.get('/fee-config', { params }),
  createFeeConfig: (data) => api.post('/fee-config', data),
  updateFeeConfig: (id, data) => api.put(`/fee-config/${id}`, data),
  deleteFeeConfig: (id) => api.delete(`/fee-config/${id}`),
};

// Payment / Receipt API
export const paymentAPI = {
  getPayments: (params) => api.get('/payments', { params }),
  getDaySummary: (params) => api.get('/payments/day-summary', { params }),
  createPayment: (data) => api.post('/payments', data),
  getPayment: (id) => api.get(`/payments/${id}`),
};

// Clinic Settings API
export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
};

// Follow-up Recall API
export const followUpAPI = {
  getFollowUpsDue: (params) => api.get('/consultations/followups/due', { params }),
};

// Medical History Master API
export const medicalHistoryAPI = {
  getConditions: (params) => api.get('/medical-history', { params }),
  createCondition: (data) => api.post('/medical-history', data),
  updateCondition: (id, data) => api.put(`/medical-history/${id}`, data),
  deleteCondition: (id) => api.delete(`/medical-history/${id}`),
};

export default api;
